import React, { useState, useEffect } from "react";
import {
  User as UserIcon,
  Building2,
  ShieldCheck,
  CreditCard,
  Save,
  Loader2,
  Plus,
  Trash2,
  Phone,
  Mail,
  Upload,
  X
} from "lucide-react";
import useAuth from "../../../../hooks/features/auth/useAuth";
import { Agent } from "../../../../types/features/Agent";
import { Entreprise } from "../../../../types/features/Entreprise";
import { alertSuccess, alertError } from "../../../../helpers/alertError";
import useEntreprises from "../../../../hooks/features/entreprises/useEntreprises";
import { uploadService } from "../../../../services/uploadService";

type SettingsTab = "profile" | "entreprise" | "security" | "billing";

export const AccountAndFacturationsPage: React.FC = () => {
  const { user , refreshUser } = useAuth();
  const { updateEntreprise, loading, createEntreprise } = useEntreprises();
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const userRole = user?.role || "agent";
  const profile = user?.profile;


  const [activeTab, setActiveTab] = useState<SettingsTab>("profile");
  const [isSaving, setIsSaving] = useState(false);

  const [entrepriseForm, setEntrepriseForm] = useState({
    name: "",
    website: "",
    address: "",
    twilio_phone_numbers: [] as string[],
    support_email: "",
    support_phone: "",
    logo: "",
  });

  const [newPhoneNumber, setNewPhoneNumber] = useState("");

  // Fonction pour formater le numéro au format E.164
  const formatPhoneNumber = (phone: string): string => {
    let cleaned = phone.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('1')) {
      cleaned = '+' + cleaned;
    }
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned;
  };

  // Charger les données de l'entreprise
  useEffect(() => {
    if (userRole === "entreprise" || userRole === "admin") {
      loadEntrepriseData();
    }
  }, [userRole]);

  const loadEntrepriseData = async () => {
    const entreprise = user?.profile as Entreprise;
    console.log("entreprise" , entreprise)
    if (entreprise) {
      setEntrepriseForm({
        name: entreprise.name || "",
        website: entreprise.website || "",
        address: entreprise.address || "",
        twilio_phone_numbers: entreprise.twilio_phone_numbers || [],
        support_email: entreprise.support_email || "",
        support_phone: entreprise.support_phone || "",
        logo: entreprise.logo || "",
      });
      if (entreprise.logo) {
        setLogoPreview(entreprise.logo);
      }
    }
  };

  const tabs = [
    { id: "profile", label: "Mon Profil", icon: UserIcon, roles: ["agent", "entreprise", "admin"] },
    { id: "entreprise", label: "Entreprise", icon: Building2, roles: ["entreprise", "admin"] },
    { id: "security", label: "Sécurité & Accès", icon: ShieldCheck, roles: ["agent", "entreprise", "admin"] },
    { id: "billing", label: "Abonnement & Facturation", icon: CreditCard, roles: ["entreprise", "admin"] },
  ].filter(tab => tab.roles.includes(userRole)) as { id: SettingsTab; label: string; icon: any }[];

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Vérifier la taille (max 5 Mo)
      if (file.size > 5 * 1024 * 1024) {
        alertError("Le logo ne doit pas dépasser 5 Mo");
        return;
      }

      // Vérifier le type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        alertError("Format non supporté. Utilisez JPG, PNG ou WEBP");
        return;
      }

      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    setEntrepriseForm(prev => ({ ...prev, logo: "" }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      if (activeTab === "entreprise") {
        let result = null;
        let logoUrl = entrepriseForm.logo;

        // Upload du logo si un nouveau fichier est sélectionné
        if (logoFile) {
          const uploadResult = await uploadService.uploadFile({
            user_id: user?.id || "",
            file: logoFile,
            type: "image",
            reason: "Logo de l'entreprise"
          });
          logoUrl = uploadResult?.url || entrepriseForm.logo;
        }

        const phoneNumbers = entrepriseForm.twilio_phone_numbers.filter(num => num.trim() !== "");

        if (profile?.id) {
          result = await updateEntreprise(profile.id, {
            name: entrepriseForm.name,
            website: entrepriseForm.website,
            address: entrepriseForm.address,
            twilio_phone_numbers: phoneNumbers,
            support_email: entrepriseForm.support_email,
            support_phone: entrepriseForm.support_phone,
            logo: logoUrl
          });
        } else {
          result = await createEntreprise({
            name: entrepriseForm.name,
            website: entrepriseForm.website,
            address: entrepriseForm.address,
            twilio_phone_numbers: phoneNumbers,
            support_email: entrepriseForm.support_email,
            support_phone: entrepriseForm.support_phone,
            call_preference: 'softphone',
            subscription_plan: "starter",
            subscription_status: "active",
            logo: logoUrl
          });
        }

        if (result) {
          alertSuccess("Paramètres enregistrés avec succès !");
          // Mettre à jour l'URL du logo dans le formulaire
          setEntrepriseForm(prev => ({ ...prev, logo: logoUrl }));
          setLogoFile(null);
          refreshUser()
        } else {
          alertError("Erreur lors de l'enregistrement");
        }
      } else {
        alertSuccess("Paramètres enregistrés avec succès !");
      }
    } catch (error) {
      console.error(error);
      alertError("Erreur lors de l'enregistrement");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEntrepriseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEntrepriseForm(prev => ({ ...prev, [name]: value }));
  };

  // Ajouter un numéro de téléphone avec formatage
  const handleAddPhoneNumber = () => {
    const formatted = formatPhoneNumber(newPhoneNumber);

    if (formatted === "+" || formatted === "") {
      alertError("Veuillez entrer un numéro de téléphone valide");
      return;
    }

    if (entrepriseForm.twilio_phone_numbers.includes(formatted)) {
      alertError("Ce numéro existe déjà");
      return;
    }

    setEntrepriseForm(prev => ({
      ...prev,
      twilio_phone_numbers: [...prev.twilio_phone_numbers, formatted]
    }));
    setNewPhoneNumber("");
  };

  // Supprimer un numéro de téléphone
  const handleRemovePhoneNumber = (index: number) => {
    setEntrepriseForm(prev => ({
      ...prev,
      twilio_phone_numbers: prev.twilio_phone_numbers.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 select-none animate-fadeIn">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-slate-100 pb-5 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Paramètres du compte</h1>
          <p className="text-sm text-slate-500">Gerez vos configurations personnelles, vos préférences d'appels et votre organisation.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-start">
        <aside className="lg:col-span-1 bg-white p-2 rounded-2xl border border-slate-100 shadow-sm space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center space-x-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${isActive
                  ? "text-primary bg-primary/5 font-semibold"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? "text-primary" : "text-slate-400"}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </aside>

        <main className="lg:col-span-3 bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
          <form onSubmit={handleSave} className="divide-y divide-slate-100">

            {activeTab === "profile" && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Informations Personnelles</h3>
                  <p className="text-xs text-slate-400">Ces informations seront visibles par les membres de votre équipe.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Prénom</label>
                    <input
                      type="text"
                      defaultValue={user?.first_name || ""}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Nom</label>
                    <input
                      type="text"
                      defaultValue={user?.last_name || ""}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Email professionnel</label>
                    <input
                      type="email"
                      disabled
                      defaultValue={user?.email || ""}
                      className="w-full px-3.5 py-2 text-sm border border-slate-100 rounded-xl bg-slate-50 text-slate-400 cursor-not-allowed"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Numéro de téléphone</label>
                    <input
                      type="tel"
                      defaultValue={user?.phonenumber || ""}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>

                  {userRole === "agent" && (
                    <div className="space-y-1.5 sm:col-span-2">
                      <label className="text-xs font-semibold text-slate-600">Département affecté</label>
                      <input
                        type="text"
                        disabled
                        defaultValue={(profile as Agent)?.departement || "Non assigné"}
                        className="w-full px-3.5 py-2 text-sm border border-slate-100 rounded-xl bg-slate-50 text-slate-400 cursor-not-allowed"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === "entreprise" && userRole !== "agent" && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Détails de l'Organisation</h3>
                  <p className="text-xs text-slate-400">Configurez l'identité globale de votre espace call center.</p>
                </div>

                {/* Logo */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-600">Logo de l'entreprise</label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative w-20 h-20 rounded-xl overflow-hidden border border-slate-200">
                        <img src={logoPreview} alt="Logo" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-slate-100 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center">
                        <Building2 size={24} className="text-slate-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="cursor-pointer">
                        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors text-sm text-slate-700">
                          <Upload size={16} />
                          {logoPreview ? "Changer le logo" : "Télécharger un logo"}
                        </div>
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/jpg,image/webp"
                          onChange={handleLogoChange}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-slate-400 mt-1">JPG, PNG, WEBP - Max 5 Mo</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-600">Nom de l'entreprise</label>
                    <input
                      type="text"
                      name="name"
                      value={entrepriseForm.name}
                      onChange={handleEntrepriseChange}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Site Web</label>
                    <input
                      type="url"
                      name="website"
                      value={entrepriseForm.website}
                      onChange={handleEntrepriseChange}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Adresse Siège social</label>
                    <input
                      type="text"
                      name="address"
                      value={entrepriseForm.address}
                      onChange={handleEntrepriseChange}
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                </div>

                {/* Support Contact */}
                <div className="pt-4 border-t border-slate-100">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Contact Support</h4>
                    <p className="text-xs text-slate-400">Ces informations seront affichées sur le formulaire public de tickets.</p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                        <Mail size={14} />
                        Email de support
                      </label>
                      <input
                        type="email"
                        name="support_email"
                        value={entrepriseForm.support_email}
                        onChange={handleEntrepriseChange}
                        placeholder="support@entreprise.com"
                        className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                        <Phone size={14} />
                        Téléphone de support
                      </label>
                      <input
                        type="tel"
                        name="support_phone"
                        value={entrepriseForm.support_phone}
                        onChange={handleEntrepriseChange}
                        placeholder="+33 1 23 45 67 89"
                        className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                      />
                    </div>
                  </div>
                </div>

                {/* Numéros Twilio */}
                <div className="pt-4 border-t border-slate-100">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800">Numéros Izi Call Center</h4>
                    <p className="text-xs text-slate-400">Ajoutez les numéros Izi Call Center associés à votre entreprise.</p>
                  </div>

                  <div className="mt-4 space-y-3">
                    {entrepriseForm.twilio_phone_numbers.length > 0 ? (
                      <div className="space-y-2">
                        {entrepriseForm.twilio_phone_numbers.map((number, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-100">
                            <div className="flex items-center space-x-3">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium text-slate-700">{number}</span>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemovePhoneNumber(index)}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-400">Aucun numéro configuré.</p>
                    )}

                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newPhoneNumber}
                        onChange={(e) => setNewPhoneNumber(e.target.value)}
                        placeholder="+1 825 963 6493"
                        className="flex-1 px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                      />
                      <button
                        type="button"
                        onClick={handleAddPhoneNumber}
                        className="p-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-xs text-slate-400">Format accepté : +1 825 963 6493</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "security" && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Authentification & Sécurité</h3>
                  <p className="text-xs text-slate-400">Mettez à jour votre mot de passe et sécurisez vos accès.</p>
                </div>

                <div className="space-y-4 max-w-md">
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Mot de passe actuel</label>
                    <input
                      type="password"
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-600">Nouveau mot de passe</label>
                    <input
                      type="password"
                      className="w-full px-3.5 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-primary/10 focus:border-primary transition-all text-slate-700"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === "billing" && userRole !== "agent" && (
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-base font-bold text-slate-800">Abonnement Actuel</h3>
                  <p className="text-xs text-slate-400">Consultez et modifiez les détails de facturation de votre licence.</p>
                </div>

                <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary bg-primary/10 px-2 py-0.5 rounded-md">
                      {(profile as Entreprise)?.subscription_plan || "Starter"}
                    </span>
                    <p className="text-sm font-semibold text-slate-800 mt-2">
                      Statut : <span className="text-emerald-600 capitalize">{(profile as Entreprise)?.subscription_status || "Active"}</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Expire le : {(profile as Entreprise)?.subscription_expires_at ? new Date((profile as Entreprise)?.subscription_expires_at!).toLocaleDateString() : "Illimité"}
                    </p>
                  </div>

                  <button type="button" className="text-xs font-bold px-4 py-2 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 text-slate-700 shadow-sm transition-colors self-start sm:self-auto">
                    Changer de plan
                  </button>
                </div>
              </div>
            )}

            <div className="p-4 bg-slate-50/50 flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center space-x-2 px-5 py-2.5 text-sm font-semibold bg-primary text-white rounded-xl shadow-md shadow-primary/20 hover:bg-primary/90 transition-all disabled:opacity-50"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{isSaving ? "Enregistrement..." : "Enregistrer les modifications"}</span>
              </button>
            </div>

          </form>
        </main>
      </div>
    </div>
  );
};